import { BasicIndex, createCollection, eq } from "@tanstack/db";
import { useLiveQuery } from "@tanstack/react-db";
import type { QueryClient } from "@tanstack/react-query";
import { queryCollectionOptions } from "@tanstack/query-db-collection";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { toScopedCollectionId } from "~/lib/db/collection-helpers";
import {
  getCollectionErrorState,
  resolveCollectionErrorPayload,
} from "~/lib/tanstack-db-collection-errors";
import {
  queryKeys,
  type AuthQueryScope,
  withAuthQueryScope,
} from "~/lib/query-keys";
import type { AppRouterContext } from "~/lib/router-runtime";
import type { PlatformApiTrpcClient } from "~/lib/trpc/platform-api-adapter";

const CANONICAL_USER_IDENTITY_STALE_TIME_MS = 5 * 60_000;
export const CURRENT_USER_IDENTITY_ROW_ID = "current-user";

export interface CurrentUserIdentityModel {
  readonly fetchedAt: number;
  readonly id: typeof CURRENT_USER_IDENTITY_ROW_ID;
  readonly userId: string;
  readonly username: string | null;
}

interface CurrentUserIdentityDependencies {
  platformApi: PlatformApiTrpcClient;
  queryClient: QueryClient;
}

interface CurrentUserIdentityCollectionOptions extends CurrentUserIdentityDependencies {
  readonly authQueryScope: AuthQueryScope;
}

export function getCurrentUserIdentityScopeToken(
  authQueryScope: AuthQueryScope,
): string {
  return authQueryScope.userId
    ? `user:${authQueryScope.userId}`
    : authQueryScope.scopeToken;
}

export function getCurrentUserIdentityQueryKey(
  authQueryScope: AuthQueryScope,
): ReadonlyArray<unknown> {
  return withAuthQueryScope(queryKeys.platformApi.currentUserIdentity, {
    ...authQueryScope,
    scopeToken: getCurrentUserIdentityScopeToken(authQueryScope),
  });
}

export function createCurrentUserIdentityCollection(
  authQueryScope: AuthQueryScope,
  dependencies: CurrentUserIdentityDependencies,
) {
  return createCollection(
    queryCollectionOptions({
      defaultIndexType: BasicIndex,
      getKey: (item: CurrentUserIdentityModel) => item.id,
      id: toScopedCollectionId(
        "auth-current-user-identity",
        getCurrentUserIdentityScopeToken(authQueryScope),
      ),
      queryClient: dependencies.queryClient,
      queryFn: async (): Promise<CurrentUserIdentityModel[]> => {
        const response =
          await dependencies.platformApi.identity.getCurrent.query();

        return [
          {
            fetchedAt: Date.now(),
            id: CURRENT_USER_IDENTITY_ROW_ID,
            userId: response.userId,
            username: response.username,
          },
        ];
      },
      queryKey: getCurrentUserIdentityQueryKey(authQueryScope),
      retry: 2,
      startSync: true,
      staleTime: CANONICAL_USER_IDENTITY_STALE_TIME_MS,
      syncMode: "on-demand",
    }),
  );
}

export type CurrentUserIdentityCollection = ReturnType<
  typeof createCurrentUserIdentityCollection
>;

interface CurrentUserIdentityCollectionCacheEntry {
  readonly collection: CurrentUserIdentityCollection;
  readonly dependencies: CurrentUserIdentityDependencies;
}

const currentUserIdentityCollectionsByAuthScope = new Map<
  string,
  CurrentUserIdentityCollectionCacheEntry
>();

export function getCurrentUserIdentityCollection(
  options: CurrentUserIdentityCollectionOptions,
): CurrentUserIdentityCollection {
  const scopeToken = getCurrentUserIdentityScopeToken(options.authQueryScope);
  const existing = currentUserIdentityCollectionsByAuthScope.get(scopeToken);
  if (existing) {
    // Auth tokens may refresh without changing the principal scope. Keep the
    // stable collection while ensuring its next fetch uses the latest client.
    existing.dependencies.platformApi = options.platformApi;
    return existing.collection;
  }

  const dependencies: CurrentUserIdentityDependencies = {
    platformApi: options.platformApi,
    queryClient: options.queryClient,
  };
  const collection = createCurrentUserIdentityCollection(
    options.authQueryScope,
    dependencies,
  );
  currentUserIdentityCollectionsByAuthScope.set(scopeToken, {
    collection,
    dependencies,
  });
  return collection;
}

export function resetCurrentUserIdentityCollectionsCache(
  authScopeToken?: string,
): void {
  if (authScopeToken) {
    currentUserIdentityCollectionsByAuthScope.delete(authScopeToken);
    return;
  }

  currentUserIdentityCollectionsByAuthScope.clear();
}

export function writeCurrentUserIdentityUsername(input: {
  readonly collection: CurrentUserIdentityCollection;
  readonly username: string;
}): boolean {
  const current = input.collection.state.get(CURRENT_USER_IDENTITY_ROW_ID);
  if (!current) {
    return false;
  }

  input.collection.utils.writeUpsert({
    ...current,
    fetchedAt: Date.now(),
    username: input.username,
  });
  return true;
}

export type CurrentUserIdentityContext = Pick<
  AppRouterContext,
  "platformApi" | "queryClient"
> & {
  readonly auth: Pick<
    AppRouterContext["auth"],
    "authQueryScope" | "authenticated" | "ready"
  >;
};

export function useCanonicalUserIdentity(
  context: CurrentUserIdentityContext,
  options: { readonly enabled: boolean },
) {
  const enabled =
    options.enabled && context.auth.ready && context.auth.authenticated;
  const identityScopeToken = getCurrentUserIdentityScopeToken(
    context.auth.authQueryScope,
  );
  const collection = useMemo(
    () =>
      getCurrentUserIdentityCollection({
        authQueryScope: context.auth.authQueryScope,
        platformApi: context.platformApi,
        queryClient: context.queryClient,
      }),
    [identityScopeToken, context.platformApi, context.queryClient],
  );
  const identityQuery = useLiveQuery({
    query: (queryBuilder) => {
      if (!enabled) {
        return undefined;
      }

      return queryBuilder
        .from({ currentUser: collection })
        .where(({ currentUser }) =>
          eq(currentUser.id, CURRENT_USER_IDENTITY_ROW_ID),
        );
    },
  });
  const collectionError = getCollectionErrorState(enabled ? collection : null);
  const isError = identityQuery.isError || collectionError.isError;
  const data = identityQuery.data?.[0];
  const refetch = useCallback(async (): Promise<void> => {
    await collection.utils.refetch({ throwOnError: true });
  }, [collection]);

  return {
    collection,
    data,
    error: resolveCollectionErrorPayload({
      error: collectionError.lastError,
      fallbackMessage: "Failed to load the current user identity.",
      isError,
    }),
    isError,
    isFetching:
      enabled && (identityQuery.isLoading || collection.utils.isFetching),
    isLoading: enabled && identityQuery.isLoading,
    isSuccess: enabled && !isError && !identityQuery.isLoading && Boolean(data),
    refetch,
    status: !enabled
      ? ("pending" as const)
      : isError
        ? ("error" as const)
        : !identityQuery.isLoading && data
          ? ("success" as const)
          : ("pending" as const),
  };
}

/**
 * Keeps canonical identity hydrated for every authenticated app route.
 * Product surfaces subscribe to this same collection rather than owning
 * route-specific identity requests.
 */
export function CurrentUserIdentityController(props: {
  readonly context: AppRouterContext;
}) {
  const currentScopeToken = getCurrentUserIdentityScopeToken(
    props.context.auth.authQueryScope,
  );
  const previousScopeRef = useRef<{
    readonly queryKey: ReadonlyArray<unknown>;
    readonly scopeToken: string;
  } | null>(null);

  useCanonicalUserIdentity(props.context, { enabled: true });

  useEffect(() => {
    const previousScope = previousScopeRef.current;
    if (previousScope && previousScope.scopeToken !== currentScopeToken) {
      resetCurrentUserIdentityCollectionsCache(previousScope.scopeToken);
      props.context.queryClient.removeQueries({
        queryKey: previousScope.queryKey,
      });
    }

    previousScopeRef.current = {
      queryKey: getCurrentUserIdentityQueryKey(
        props.context.auth.authQueryScope,
      ),
      scopeToken: currentScopeToken,
    };
  }, [
    currentScopeToken,
    props.context.auth.authQueryScope,
    props.context.queryClient,
  ]);

  return null;
}
